import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../utils/apiClient";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Send,
  FileText,
  Upload,
  MessageSquare,
  BookOpen,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";

export default function AgentChat() {
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [stats, setStats] = useState(null);  // Store stats separately
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [expandedCitations, setExpandedCitations] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadWorkspace();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadWorkspace = async () => {
    try {
      const res = await apiClient.get("/agent/workspace/me");
      setWorkspace(res.data.workspace);
      setStats(res.data.stats);  // Store stats separately
    } catch (err) {
      toast.error("Failed to load workspace");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || sending) return;

    const userMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: inputValue,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setSending(true);

    try {
      const res = await apiClient.post("/agent/chat/message", {
        content: userMessage.content,
        session_id: sessionId,
      });

      // Update session ID if new
      if (!sessionId) {
        setSessionId(res.data.session_id);
      }

      // Add assistant response
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== userMessage.id),
        { ...userMessage, id: `user-${Date.now()}` },
        res.data,
      ]);
    } catch (err) {
      toast.error("Failed to send message");
      // Remove temporary message
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleCitations = (messageId) => {
    setExpandedCitations((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const hasDocuments = stats?.document_count > 0;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="agent-chat-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-primary" />
              {workspace?.agent_name || "Your Contract Expert"}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Your 24/7 labor relations advisor - ask anything about your agreements
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/agent/documents")}
            data-testid="manage-documents-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Documents ({stats?.document_count || 0})
          </Button>
        </div>

        {/* No documents warning */}
        {!hasDocuments && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-800 dark:text-amber-200">
                  No documents uploaded yet
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Upload your collective agreements, contracts, or policies to start asking questions.
                </p>
                <Button
                  size="sm"
                  className="mt-3"
                  onClick={() => navigate("/agent/documents")}
                  data-testid="upload-first-doc-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Documents
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="bg-card border border-border rounded-lg min-h-[500px] flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">Start a conversation</p>
                <p className="text-sm mt-1">
                  Ask questions about your contracts, policies, or agreements
                </p>
                {hasDocuments && (
                  <div className="mt-6 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Try asking:
                    </p>
                    {[
                      "What are the overtime provisions?",
                      "Summarize the grievance procedure",
                      "What are the seniority rules?",
                    ].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setInputValue(q)}
                        className="block w-full max-w-md mx-auto text-left px-4 py-2 bg-secondary/50 hover:bg-secondary rounded-lg text-sm transition"
                        data-testid={`suggested-q-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${idx}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg p-4 ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>

                    {/* Citations */}
                    {msg.citations && msg.citations.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <button
                          onClick={() => toggleCitations(msg.id)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
                          data-testid={`toggle-citations-${idx}`}
                        >
                          <FileText className="w-3 h-3" />
                          {msg.citations.length} source{msg.citations.length > 1 ? "s" : ""}
                          {expandedCitations[msg.id] ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </button>

                        {expandedCitations[msg.id] && (
                          <div className="mt-2 space-y-2">
                            {msg.citations.map((cite, ci) => (
                              <div
                                key={ci}
                                className="bg-background/50 rounded p-2 text-xs"
                              >
                                <p className="font-medium">
                                  {cite.document_name}
                                  {cite.section && `, ${cite.section}`}
                                  {cite.page_number && `, Page ${cite.page_number}`}
                                </p>
                                <p className="text-muted-foreground mt-1 line-clamp-2">
                                  {cite.content}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-lg p-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-border p-4">
            <div className="flex gap-2">
              <Textarea
                placeholder={
                  hasDocuments
                    ? "Ask a question about your contracts..."
                    : "Upload documents first to start asking questions"
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending || !hasDocuments}
                className="min-h-[48px] max-h-[200px] resize-none"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || sending || !hasDocuments}
                className="px-4"
                data-testid="send-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Answers are based only on your uploaded documents. Press Enter to send.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
